<template>
  <div class="hot-deals-container">
    <div v-if="loading" class="loading-indicator" :class="{ 'loading-indicator--first': firstLoad }">
      <div class="spinner"></div>
      <p>Finding the hottest cruise deals...</p>
    </div>
    
    <div v-else-if="error" class="error-message">
      <p>{{ error }}</p>
      <button @click="$emit('refresh')" class="retry-button">Try Again</button>
    </div>
    
    <!-- CP-127: filters returned no deals: recovery instead of a dead end. -->
    <FilterEmptyRecovery
      v-else-if="filteredEmpty"
      type="deals"
      :options="recoveryOptions"
      @clear-all="$emit('clear-filters')"
      @relax="$emit('relax-filter', $event)"
    />

    <div v-else>
      <!-- Price Drop Deals Section. Only real peak+drop rows; honest empty otherwise. -->
      <div v-if="priceDropsEnabled" class="deals-section">
        <h3 class="section-title">
          <span class="section-icon">🔥</span>
          {{ priceDropSectionTitle }}
        </h3>
        <p v-if="priceDropOverlapNote" class="section-note" role="note">{{ priceDropOverlapNote }}</p>
        <div v-if="realPriceDrops.length > 0" class="deals-grid">
          <div v-for="deal in realPriceDrops" :key="`drop-${deal.id}`" class="deal-card">
            <div v-if="showPriceDropBadge(deal)" class="deal-badge price-drop">PRICE DROP</div>
            <div v-if="hasDealPrice(deal) && isNewDeal(deal)" class="deal-badge new-badge">NEW</div>
            <div class="deal-header">
              <h3 class="cs-title-case">{{ titleCase(deal.name) }}</h3>
              <p class="deal-subline">
                <span v-if="deal.ship_name">{{ deal.ship_name }}</span>
                <span v-if="deal.ship_name && deal.cruise_line" aria-hidden="true"> · </span>
                <span class="cs-line-pill">{{ deal.cruise_line }}</span>
              </p>
            </div>
            <div class="deal-details deal-meta-row">
              <p class="deal-meta">
                <template v-if="deal.departure_port">From {{ deal.departure_port }}</template>
                <template v-if="deal.departure_port && deal.duration"> · </template>
                <template v-if="deal.duration">{{ nightsLabel(deal.duration) }}</template>
                <template v-if="deal.departure_port || deal.duration"> · </template>
                {{ formatDate(deal.sail_date || deal.departure_date) }}
              </p>
            </div>
            <div class="deal-pricing">
              <template v-if="hasDealPrice(deal)">
                <div v-if="deal.peak_price" class="old-price">${{ formatPrice(deal.peak_price || deal.previous_price) }} <span v-if="deal.peak_cabin_type" class="cabin-type">({{ deal.peak_cabin_type }})</span></div>
                <div class="current-price">${{ formatPrice(deal.current_price) }} <span v-if="deal.current_cabin_type" class="cabin-type">({{ deal.current_cabin_type }})</span></div>
                <div class="price-freshness">{{ priceCue(deal) }}</div>
                <div v-if="deal.price_drop" class="savings">
                  Save ${{ formatPrice(deal.price_drop) }} ({{ Math.round(deal.price_drop_percentage) }}%)
                </div>
              </template>
              <template v-else>
                <div class="current-price price-unavailable">{{ hiddenPriceReason(deal) }}</div>
                <div v-if="staleAgeStamp(deal)" class="price-freshness">{{ staleAgeStamp(deal) }}</div>
              </template>
            </div>
            <div class="deal-actions">
              <router-link :to="getCruiseUrl(deal.id, deal.sail_date)" class="view-deal-btn" :aria-label="sailingLinkAriaLabel(deal)">{{ sailingLinkText(deal) }}</router-link>
              <a v-affiliate-link
                v-if="canBook(deal.cruise_line)"
                :href="affiliateUrl(deal.id, 'hot')"
                target="_blank"
                rel="noopener noreferrer sponsored"
                class="cs-btn-book-outline deal-book-btn"
                :aria-label="ctaAriaLabel(deal)"
              >
                {{ tierCta(deal) }} <i class="fas fa-external-link-alt" aria-hidden="true"></i>
              </a>
              <span v-else class="cs-btn-book-disabled deal-book-quiet" role="status" title="Direct booking is not available on this site">Not Bookable Here</span>
            </div>
            <p v-if="canBook(deal.cruise_line)" class="deal-affiliate-note">{{ tierDealNote(deal) }}</p>
          </div>
        </div>
        <p v-else class="section-empty">No sailings are priced below a recorded 30-day peak right now.</p>
      </div>
      
      <!-- Best Value Deals Section -->
      <div v-if="bestValueDeals.length > 0" class="deals-section">
        <h3 class="section-title">
          <span class="section-icon">💰</span>
          {{ bestValueSectionTitle }}
        </h3>
        <div class="deals-grid">
          <div v-for="deal in bestValueDeals" :key="`value-${deal.id}`" class="deal-card">
            <div v-if="hasDealPrice(deal)" class="deal-badge best-value">BEST VALUE</div>
            <div class="deal-header">
              <h3 class="cs-title-case">{{ titleCase(deal.name) }}</h3>
              <p class="deal-subline">
                <span v-if="deal.ship_name">{{ deal.ship_name }}</span>
                <span v-if="deal.ship_name && deal.cruise_line" aria-hidden="true"> · </span>
                <span class="cs-line-pill">{{ deal.cruise_line }}</span>
              </p>
            </div>
            <div class="deal-details deal-meta-row">
              <p class="deal-meta">
                <template v-if="deal.departure_port">From {{ deal.departure_port }}</template>
                <template v-if="deal.departure_port && deal.duration"> · </template>
                <template v-if="deal.duration">{{ nightsLabel(deal.duration) }}</template>
                <template v-if="deal.departure_port || deal.duration"> · </template>
                {{ formatDate(deal.sail_date || deal.departure_date) }}
              </p>
            </div>
            <div class="deal-pricing">
              <template v-if="hasDealPrice(deal)">
                <div class="current-price">${{ formatPrice(deal.current_price) }} <span v-if="deal.current_cabin_type" class="cabin-type">({{ deal.current_cabin_type }})</span></div>
                <div class="price-freshness">{{ priceCue(deal) }}</div>
                <div v-if="deal.price_per_night" class="price-per-night">
                  Just ${{ formatPrice(deal.price_per_night) }} per night
                </div>
              </template>
              <template v-else>
                <div class="current-price price-unavailable">{{ hiddenPriceReason(deal) }}</div>
                <div v-if="staleAgeStamp(deal)" class="price-freshness">{{ staleAgeStamp(deal) }}</div>
              </template>
            </div>
            <div class="deal-actions">
              <router-link :to="getCruiseUrl(deal.id, deal.sail_date)" class="view-deal-btn" :aria-label="sailingLinkAriaLabel(deal)">{{ sailingLinkText(deal) }}</router-link>
              <a v-affiliate-link
                v-if="canBook(deal.cruise_line)"
                :href="affiliateUrl(deal.id, 'hot')"
                target="_blank"
                rel="noopener noreferrer sponsored"
                class="cs-btn-book-outline deal-book-btn"
                :aria-label="ctaAriaLabel(deal)"
              >
                {{ tierCta(deal) }} <i class="fas fa-external-link-alt" aria-hidden="true"></i>
              </a>
              <span v-else class="cs-btn-book-disabled deal-book-quiet" role="status" title="Direct booking is not available on this site">Not Bookable Here</span>
            </div>
            <p v-if="canBook(deal.cruise_line)" class="deal-affiliate-note">{{ tierDealNote(deal) }}</p>
          </div>
        </div>
      </div>
      
      <!-- Newly Listed Section -->
      <div v-if="newlyListed.length > 0" class="deals-section">
        <h3 class="section-title">
          <span class="section-icon">⚡</span>
          Newly Listed Cruises
        </h3>
        <div class="deals-grid">
          <div v-for="deal in newlyListed" :key="`newly-${deal.id}`" class="deal-card">
            <div v-if="hasDealPrice(deal)" class="deal-badge newly-listed">NEWLY LISTED</div>
            <div class="deal-header">
              <h3 class="cs-title-case">{{ titleCase(deal.name) }}</h3>
              <p class="deal-subline">
                <span v-if="deal.ship_name">{{ deal.ship_name }}</span>
                <span v-if="deal.ship_name && deal.cruise_line" aria-hidden="true"> · </span>
                <span class="cs-line-pill">{{ deal.cruise_line }}</span>
              </p>
            </div>
            <div class="deal-details deal-meta-row">
              <p class="deal-meta">
                <template v-if="deal.departure_port">From {{ deal.departure_port }}</template>
                <template v-if="deal.departure_port && deal.duration"> · </template>
                <template v-if="deal.duration">{{ nightsLabel(deal.duration) }}</template>
                <template v-if="deal.departure_port || deal.duration"> · </template>
                {{ formatDate(deal.sail_date || deal.departure_date) }}
              </p>
            </div>
            <div class="deal-pricing">
              <template v-if="hasDealPrice(deal)">
                <div v-if="deal.peak_price" class="old-price">${{ formatPrice(deal.peak_price) }} <span v-if="deal.peak_cabin_type" class="cabin-type">({{ deal.peak_cabin_type }})</span></div>
                <div class="current-price">${{ formatPrice(deal.current_price) }} <span v-if="deal.current_cabin_type" class="cabin-type">({{ deal.current_cabin_type }})</span></div>
                <div class="price-freshness">{{ priceCue(deal) }}</div>
                <div v-if="deal.discount_percent" class="savings">
                  Save {{ deal.discount_percent }}%
                </div>
              </template>
              <template v-else>
                <div class="current-price price-unavailable">{{ hiddenPriceReason(deal) }}</div>
                <div v-if="staleAgeStamp(deal)" class="price-freshness">{{ staleAgeStamp(deal) }}</div>
              </template>
            </div>
            <div class="deal-actions">
              <router-link :to="getCruiseUrl(deal.id, deal.sail_date)" class="view-deal-btn" :aria-label="sailingLinkAriaLabel(deal)">{{ sailingLinkText(deal) }}</router-link>
              <a v-affiliate-link
                v-if="canBook(deal.cruise_line)"
                :href="affiliateUrl(deal.id, 'hot')"
                target="_blank"
                rel="noopener noreferrer sponsored"
                class="cs-btn-book-outline deal-book-btn"
                :aria-label="ctaAriaLabel(deal)"
              >
                {{ tierCta(deal) }} <i class="fas fa-external-link-alt" aria-hidden="true"></i>
              </a>
              <span v-else class="cs-btn-book-disabled deal-book-quiet" role="status" title="Direct booking is not available on this site">Not Bookable Here</span>
            </div>
            <p v-if="canBook(deal.cruise_line)" class="deal-affiliate-note">{{ tierDealNote(deal) }}</p>
          </div>
        </div>
      </div>
      
      <div v-if="!priceDropsEnabled && bestValueDeals.length === 0 && newlyListed.length === 0" class="no-deals">
        <p>No hot deals available at the moment. Please check back later!</p>
      </div>
    </div>
  </div>
</template>

<script>
import { canBookLine } from '@/utils/affiliateFlag';
import { dealNote, tierCtaLabel } from '@/utils/bookingTierCopy';
import { actionAriaLabel, sailingLinkAriaLabel, sailingLinkText } from '@/utils/sailingLinkLabel';
import { hiddenPriceReason, listingPriceCue, showsListingPrice, staleAgeStamp } from '@/utils/priceFreshness';
import { priceDropBadgeVisible, realPriceDropDeals } from '@/utils/priceDropBadge';
import { priceDropSectionTitle, priceDropOverlapNote } from '@/utils/hotPriceDrops';
import FilterEmptyRecovery from '@/components/FilterEmptyRecovery.vue';

import { formatPriceAmount } from '@/utils/formatMoney'
import { formatPlaceTitle } from '@/utils/formatPlaceTitle';
export default {
  name: 'HotDealsList',
  components: { FilterEmptyRecovery },
  emits: ['refresh', 'clear-filters', 'relax-filter'],
  props: {
    priceDropDeals: {
      type: Array,
      default: () => []
    },
    bestValueDeals: {
      type: Array,
      default: () => []
    },
    newlyListed: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    // Deals CLS: initial load only (HotDeals.vue: nothing has settled yet). Makes the loader taller than
    // the viewport so the footer stays below the fold until the list arrives.
    firstLoad: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    },
    affiliateEnabled: {
      type: Boolean,
      default: false
    },
    priceDropsEnabled: {
      type: Boolean,
      default: true
    },
    // CP-127: active filters left every enabled section empty.
    filteredEmpty: {
      type: Boolean,
      default: false
    },
    // [{ key, label, patch }] from dealRelaxOptions, most restrictive first.
    recoveryOptions: {
      type: Array,
      default: () => []
    },
    // CP-95: Hot page excluded Home shelf membership — title must not claim
    // these are still the Home "Biggest" / primary Best Value set.
    differentiatedFromHome: {
      type: Boolean,
      default: false
    },
    // CP-237: API showed every honest drop because skipping Home's window
    // would have emptied the section. Not "More" — same sailings as Home.
    priceDropsOverlapHome: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    realPriceDrops() {
      return realPriceDropDeals(this.priceDropDeals);
    },
    priceDropSectionTitle() {
      return priceDropSectionTitle(this.differentiatedFromHome, this.priceDropsOverlapHome);
    },
    priceDropOverlapNote() {
      return priceDropOverlapNote(this.priceDropsOverlapHome, this.realPriceDrops.length);
    },
    bestValueSectionTitle() {
      return this.differentiatedFromHome
        ? 'More Best Value Cruises'
        : 'Best Value Cruises';
    }
  },
  methods: {
    // CP-212: one pattern for every sailing link (visible text + ship/date accessible name).
    sailingLinkText,
    sailingLinkAriaLabel,
    // CP-212: the CruiseDirect link's accessible name leads with its visible text, then the sailing.
    ctaAriaLabel(deal) {
      return actionAriaLabel(this.tierCta(deal), deal, '(opens in a new tab)');
    },
    // CP-143: ON-state copy from the landing tier (only reached when canBook is true).
    tierCta(deal) {
      return tierCtaLabel(deal && deal.affiliate_landing_tier);
    },
    tierDealNote(deal) {
      return dealNote({
        tier: deal && deal.affiliate_landing_tier,
        line: deal && deal.cruise_line,
        collectedAt: deal && deal.price_updated_at,
      });
    },
    // CP-141: CTA only when the flag is on and CruiseDirect sells the line.
    canBook(line) {
      return canBookLine(this.affiliateEnabled, line);
    },
    formatDate(dateString) {
      if (!dateString) return 'TBD';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    },
    formatPrice(price) {
      return formatPriceAmount(price);
    },
    hasDealPrice(deal) {
      return showsListingPrice(deal);
    },
    showPriceDropBadge(deal) {
      return priceDropBadgeVisible(deal);
    },
    priceCue(deal) {
      return listingPriceCue(deal);
    },
    staleAgeStamp,
    hiddenPriceReason,
    getCruiseUrl(cruiseId, sailDate) {
      let url = `/cruise/${cruiseId}`;
      if (sailDate) {
        url += `?sail_date=${encodeURIComponent(sailDate)}`;
      }
      return url;
    },
    affiliateUrl(cruiseId, source) {
      return `/api/affiliate/redirect?cruise_id=${cruiseId}&partner=cruisedirect&source=${source}`;
    },
    // eslint-disable-next-line no-unused-vars
    isNewDeal(deal) {
      return false;
    },
    titleCase(str) {
      return formatPlaceTitle(str);
    },
    nightsLabel(n) {
      const num = Number(n);
      if (!Number.isFinite(num) || num <= 0) return '';
      const rounded = Math.round(num);
      return rounded === 1 ? '1 night' : `${rounded} nights`;
    }
  }
};
</script>

<style scoped>
.hot-deals-container {
  margin: var(--space-5) 0;
  padding: 0;
  background: transparent;
}

.section-title {
  color: var(--cs-ink);
  margin: var(--space-4) 0;
  font-size: 1.25rem;
  font-family: var(--font-heading);
  border-bottom: 2px solid var(--cs-card-border);
  padding-bottom: var(--space-2);
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.section-icon { font-size: 1.4rem; }

.deals-section { margin-bottom: var(--space-6); }

.deals-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-4);
}

@media (min-width: 768px) {
  .deals-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--space-5);
  }
}

@media (min-width: 1024px) {
  .deals-grid {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  }
}

.deal-card {
  position: relative;
  background: var(--cs-card);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-card);
  border: 1px solid var(--cs-card-border);
  padding: var(--space-5) var(--space-4) var(--space-4);
  transition: transform var(--transition-normal), box-shadow var(--transition-normal);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.deal-card:hover {
  transform: translateY(-3px);
  box-shadow: var(--shadow-card-hover);
}

.deal-badge {
  position: absolute;
  top: 0;
  right: 0;
  padding: 0.4rem 0.8rem;
  font-size: 0.7rem;
  font-weight: 700;
  color: white;
  border-bottom-left-radius: 8px;
  z-index: 1;
}

.price-drop { background-color: var(--cs-deal-accent); }
.best-value { background-color: var(--cs-price-fill); }
.newly-listed { background-color: var(--cs-amber); color: var(--cs-on-amber); }
.new-badge { top: 2.5rem; background-color: var(--cs-fill); font-size: 0.6rem; }

.deal-header {
  margin-bottom: var(--space-3);
  padding-top: var(--space-4);
  padding-right: 5.5rem; /* avoid badge overlap */
}

.deal-header h4 {
  color: var(--cs-muted);
  margin: 0;
  font-size: var(--text-small);
  font-weight: 600;
}

.deal-header h3 {
  margin: 0.25rem 0 0;
  font-size: 18px;
  line-height: 24px;
  color: var(--cs-ink);
  font-weight: 600;
}

.deal-details {
  margin-bottom: var(--space-3);
  font-size: var(--text-small);
}

.deal-details p {
  margin: 0.25rem 0;
  color: var(--cs-muted);
}

.deal-pricing {
  margin-bottom: var(--space-4);
  padding: var(--space-3);
  background-color: var(--color-accent-success-bg);
  border-radius: var(--radius-md);
}

.old-price {
  text-decoration: line-through;
  color: var(--cs-muted);
  font-size: var(--text-small);
}

.current-price {
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--cs-price);
  margin: 0.25rem 0;
}

.savings {
  color: var(--cs-savings-text);
  font-weight: 700;
  font-size: var(--text-small);
}

.price-per-night {
  color: var(--cs-price);
  font-weight: 700;
  font-size: var(--text-small);
}

.cabin-type {
  font-size: 0.75rem;
  color: var(--cs-muted);
  font-weight: normal;
  font-style: italic;
}

.view-deal-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  /* CP-212: labels are now "See N-night <destination> sailing" and can wrap to two lines in a narrow card.
     Every button reserves the two-line height (2 x 22px + 2 x 12px padding) so cards in a row stay aligned
     and the skeleton matches the loaded card. */
  min-height: var(--card-link-min-height);
  text-wrap: balance;
  padding: var(--space-3);
  background-color: var(--cs-fill);
  color: white;
  text-align: center;
  border-radius: var(--radius-md);
  text-decoration: none;
  font-weight: 700;
  transition: background-color var(--transition-normal);
  box-sizing: border-box;
}

.view-deal-btn:hover {
  background-color: var(--cs-fill-hover);
}

.deal-actions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: var(--space-2);
  margin-top: auto;
}

.deal-book-btn {
  width: 100%;
  box-sizing: border-box;
}

.deal-book-quiet {
  align-self: center;
  margin-top: 2px;
}

.deal-affiliate-note {
  margin: var(--space-2) 0 0;
  font-size: 11px;
  color: var(--cs-muted);
  line-height: 1.4;
}



.loading-indicator {
  text-align: center;
  padding: var(--space-6);
  color: var(--cs-muted);
}

/* Deals CLS: same as Best Deals / CP-230 Browse. First load only: the loader is taller than the viewport so
   footer.main-footer starts below the fold and does not jump when the lists render. Spinner stays at the
   top (box is block, content flows from the top). Exists only under v-if="loading" and only until the
   first fetch settles, so a short or empty result keeps its natural height. */
.hot-deals-container > .loading-indicator--first {
  min-height: 100vh;
  min-height: 100svh;
}

.spinner {
  display: inline-block;
  width: 40px;
  height: 40px;
  border: 4px solid var(--color-border-light);
  border-radius: 50%;
  border-top-color: var(--cs-signal-teal);
  animation: spin 1s ease-in-out infinite;
  margin-bottom: var(--space-4);
}

@keyframes spin { to { transform: rotate(360deg); } }

.error-message {
  text-align: center;
  color: var(--color-error);
  background: var(--color-error-bg);
  padding: var(--space-5);
  border-radius: var(--radius-lg);
}

.retry-button {
  margin-top: var(--space-4);
  min-height: var(--touch-min);
  padding: var(--space-3) var(--space-5);
  background-color: var(--cs-fill);
  color: white;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-weight: 600;
}

.retry-button:hover { background-color: var(--cs-fill-hover); }

.no-deals {
  text-align: center;
  padding: var(--space-6);
  color: var(--cs-muted);
  background: var(--cs-signal-mist);
  border-radius: var(--radius-lg);
}

.section-note {
  margin: calc(-1 * var(--space-2)) 0 var(--space-3);
  color: var(--cs-muted);
  font-size: var(--text-small);
  line-height: var(--line-height-body);
}

.section-empty {
  margin: 0;
  padding: var(--space-4);
  color: var(--cs-muted);
  background: var(--cs-signal-mist);
  border-radius: var(--radius-lg);
}

.deal-subline {
  margin: var(--space-2) 0 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  font-size: 13px;
  line-height: 18px;
  color: var(--cs-muted);
}
.deal-meta-row {
  padding-top: 0;
}
.deal-meta {
  margin: 0;
  font-size: var(--text-small);
  color: var(--cs-muted);
  line-height: 1.45;
}
.deal-header h3 {
  margin: 0;
}
.price-freshness {
  margin-top: var(--space-2);
  font-size: var(--text-caption);
  line-height: 1.35;
  color: var(--color-text-muted);
}

.price-unavailable {
  color: var(--color-text-muted);
  font-style: italic;
  font-size: var(--text-small);
  font-weight: 600;
}
</style>
